import { apiRequest } from './api';

export interface ReleaseSummaryResponse {
  id: number;
  slug: string;
  title: string;
  release_type: string;
  status: 'scheduled' | 'live' | 'sold_out';
  cover_url: string | null;
  price_cents: number | null;
  currency: string;
  pwyw_min_cents: number | null;
  tipping_enabled: number;
  member_only: number;
  publish_at: string | null;
  lanes: string | null;
  city: string | null;
  limited_total: number | null;
  limited_sold: number;
  artist_name: string;
  artist_slug: string;
  owned_release?: number | null;
  complete_price_cents?: number | null;
  owned_track_total_cents?: number;
  artists?: TrackArtistResponse[];
}

export interface TrackArtistResponse {
  id: number;
  name: string;
  slug: string;
}

export interface ReleaseTrackResponse {
  id: number;
  disc_no: number;
  track_no: number;
  title: string;
  duration_sec: number | null;
  has_preview: number;
  owned?: number | null;
  price_cents: number | null;
  release_only?: number;
  likes_count?: number | null;
  artists?: TrackArtistResponse[];
  track_artists?: TrackArtistResponse[];
}

export interface ReleaseDetailResponse extends ReleaseSummaryResponse {
  description: string | null;
  price_cents: number | null;
  pwyw_min_cents: number | null;
  tipping_enabled: number;
  member_only: number;
  limited_total: number | null;
  limited_sold: number;
  tracks: ReleaseTrackResponse[];
  lanes: string | null;
  complete_price_cents?: number | null;
  owned_track_ids?: number[];
  owned_track_total_cents?: number;
}

export interface ReleasesIndexResponse {
  releases: ReleaseSummaryResponse[];
}

export interface FetchReleasesParams {
  artist?: string;
  lane?: string | string[];
  type?: string | string[];
  city?: string | string[];
  format?: string | string[];
  features?: string | string[];
  priceMin?: number;
  priceMax?: number;
  sort?: string;
  page?: number;
  perPage?: number;
}

export interface TrackSummaryResponse extends ReleaseSummaryResponse {
  track_id: number;
  track_title: string;
  disc_no: number;
  track_no: number;
  duration_sec: number | null;
  has_preview: number;
  likes_count?: number | null;
  track_artists?: TrackArtistResponse[];
  owned_track?: number | null;
}

export interface TracksIndexResponse {
  tracks: TrackSummaryResponse[];
}

export async function fetchReleases(params: FetchReleasesParams = {}, authToken?: string) {
  const query = buildFilterQuery(params);
  const queryString = query.toString();
  const path = `/releases${queryString ? `?${queryString}` : ''}`;
  return apiRequest<ReleasesIndexResponse>(path, authToken ? { authToken } : undefined);
}

export type FetchTracksParams = FetchReleasesParams;

export async function fetchTracks(params: FetchTracksParams = {}, authToken?: string) {
  const query = buildFilterQuery(params);
  const queryString = query.toString();
  const path = `/tracks${queryString ? `?${queryString}` : ''}`;
  return apiRequest<TracksIndexResponse>(path, authToken ? { authToken } : undefined);
}

export async function fetchReleaseDetail(slug: string, authToken?: string) {
  return apiRequest<ReleaseDetailResponse>(`/releases/${slug}`, {
    authToken,
  });
}

function buildFilterQuery(params: FetchReleasesParams) {
  const query = new URLSearchParams();
  const appendMulti = (key: string, value?: string | string[]) => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.filter(Boolean).forEach((entry) => query.append(key, entry));
    } else {
      query.append(key, value);
    }
  };

  if (params.artist) query.set('artist', params.artist);
  appendMulti('lane', params.lane);
  appendMulti('type', params.type);
  appendMulti('city', params.city);
  appendMulti('format', params.format);
  appendMulti('features', params.features);

  if (typeof params.priceMin === 'number') {
    query.set('price_min', String(params.priceMin));
  }
  if (typeof params.priceMax === 'number') {
    query.set('price_max', String(params.priceMax));
  }
  if (params.sort) {
    query.set('sort', params.sort);
  }
  if (typeof params.page === 'number') {
    query.set('page', String(params.page));
  }
  if (typeof params.perPage === 'number') {
    query.set('per_page', String(params.perPage));
  }
  return query;
}
